/**
 * Drives the sign-in flow for the single email+password form, backed by the
 * Laravel native-auth endpoints (server-mediated token acquisition).
 *
 * Flow: signIn(email,password) ->
 *   - completed     -> tokens available (used as bearer, as today)
 *   - mfa_required  -> show OTP screen -> submitMfaCode()
 *   - failed        -> error
 */
import {useCallback, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {AuthTokens, MfaChallengeInfo, mfaResend, nativeLogin, nativeMfa} from './nativeAuthApi';
import {extractErrorCode, nativeAuthErrorMessage} from './errorMessages';

export type SignInStatus =
  | 'idle'
  | 'submitting'
  | 'mfaRequired'
  | 'verifyingMfa'
  | 'passwordResetRequired'
  | 'completed'
  | 'failed';

export interface UseSignIn {
  status: SignInStatus;
  error?: string;
  mfa?: MfaChallengeInfo;
  tokens?: AuthTokens;
  /** Why a reset is required ('expired' | 'migration'), when status is passwordResetRequired. */
  resetReason?: string;
  signIn: (email: string, password: string) => Promise<void>;
  submitMfaCode: (code: string) => Promise<void>;
  resendMfaCode: () => Promise<void>;
  reset: () => void;
}

export const useSignIn = (): UseSignIn => {
  const intl = useIntl();
  const [status, setStatus] = useState<SignInStatus>('idle');
  const [errorCode, setErrorCode] = useState<string>();
  const [mfa, setMfa] = useState<MfaChallengeInfo>();
  const [tokens, setTokens] = useState<AuthTokens>();
  const [resetReason, setResetReason] = useState<string>();
  const continuationRef = useRef<string | null>(null);
  const methodIdRef = useRef<string | null>(null);

  // Continuation token expired mid-MFA. We don't keep the password, so we can't
  // silently re-login — send the user back to the login form with a clear message
  // (rather than a misleading "wrong code"). The form stays filled for a quick retry.
  const bounceToLoginExpired = useCallback((): void => {
    continuationRef.current = null;
    methodIdRef.current = null;
    setMfa(undefined);
    setErrorCode('flow_expired');
    setStatus('failed');
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    setErrorCode(undefined);
    setStatus('submitting');
    try {
      const result = await nativeLogin(email, password);
      if (result.status === 'completed') {
        setTokens({
          accessToken: result.accessToken,
          idToken: result.idToken,
          refreshToken: result.refreshToken,
          expiresIn: result.expiresIn,
        });
        setStatus('completed');
      } else if (result.status === 'password_reset_required') {
        // Entra requires a password reset before sign-in.
        setResetReason(result.reason);
        setStatus('passwordResetRequired');
      } else {
        continuationRef.current = result.continuationToken;
        methodIdRef.current = result.challenge.methodId ?? null;
        setMfa(result.challenge);
        setStatus('mfaRequired');
      }
    } catch (e) {
      setErrorCode(extractErrorCode(e));
      setStatus('failed');
    }
  }, []);

  const submitMfaCode = useCallback(
    async (code: string): Promise<void> => {
      if (!continuationRef.current) {
        return;
      }
      setErrorCode(undefined);
      setStatus('verifyingMfa');
      try {
        const result = await nativeMfa(continuationRef.current, code);
        if (result.status !== 'completed') {
          // Expired / force-change password surfaces only at token issuance (after MFA).
          setResetReason(result.reason);
          setStatus('passwordResetRequired');
          return;
        }
        setTokens({
          accessToken: result.accessToken,
          idToken: result.idToken,
          refreshToken: result.refreshToken,
          expiresIn: result.expiresIn,
        });
        setStatus('completed');
      } catch (e) {
        const code = extractErrorCode(e);
        if (code === 'flow_expired') {
          bounceToLoginExpired();
          return;
        }
        setErrorCode(code);
        setStatus('mfaRequired');
      }
    },
    [bounceToLoginExpired]
  );

  const resendMfaCode = useCallback(async (): Promise<void> => {
    if (!continuationRef.current || !methodIdRef.current) {
      return;
    }
    setErrorCode(undefined);
    try {
      const result = await mfaResend(continuationRef.current, methodIdRef.current);
      continuationRef.current = result.continuationToken;
      methodIdRef.current = result.challenge.methodId ?? methodIdRef.current;
      setMfa(result.challenge);
      setStatus('mfaRequired');
    } catch (e) {
      const code = extractErrorCode(e);
      if (code === 'flow_expired') {
        bounceToLoginExpired();
        return;
      }
      setErrorCode(code);
    }
  }, [bounceToLoginExpired]);

  const reset = useCallback((): void => {
    continuationRef.current = null;
    methodIdRef.current = null;
    setErrorCode(undefined);
    setMfa(undefined);
    setTokens(undefined);
    setResetReason(undefined);
    setStatus('idle');
  }, []);

  const error = errorCode ? nativeAuthErrorMessage(intl, errorCode) : undefined;

  return {status, error, mfa, tokens, resetReason, signIn, submitMfaCode, resendMfaCode, reset};
};
